import { Trans } from "@lingui/macro";
import { X } from "lucide-react";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";

interface TermsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const TermsModal: React.FC<TermsModalProps> = ({ open, onOpenChange }) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl w-[95vw] h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
                    <h2 className="text-lg font-semibold"><Trans>Terms of Service</Trans></h2>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="max-w-3xl mx-auto text-sm text-foreground">
                        <h1 className="text-2xl font-bold mb-6">NEXTPOD 利用規約</h1>

                        <p className="mb-4 leading-relaxed">本利用規約（以下「本規約」といいます）は、株式会社JSecurity（以下「当社」といいます）が提供するNEXTPOD（以下「本サービス」といいます）の利用条件を定めるものです。本サービスをご利用いただくすべてのお客様（以下「ユーザー」といいます）は、本規約に同意したものとみなされます。</p>

                        <h2 className="text-lg font-bold mt-8 mb-3">第1条（適用）</h2>
                        <ol className="list-decimal list-inside space-y-2 mb-4">
                            <li>本規約は、本サービスの提供条件及び本サービスの利用に関する当社とユーザーとの間の権利義務関係を定めることを目的とし、ユーザーと当社との間の本サービスの利用に関わる一切の関係に適用されます。</li>
                            <li>当社が本サービス上で掲載する本サービス利用に関するルール等は、本規約の一部を構成するものとします。</li>
                        </ol>

                        <h2 className="text-lg font-bold mt-8 mb-3">第2条（定義）</h2>
                        <p className="mb-2">本規約において使用する以下の用語は、各々以下に定める意味を有するものとします。</p>
                        <ol className="list-decimal list-inside space-y-2 mb-4">
                            <li>「本サービス」とは、当社が提供するNEXTPODという名称のビジネスコミュニケーションサービス（理由の如何を問わずサービスの名称又は内容が変更された場合は、当該変更後のサービスを含みます）を意味します。</li>
                            <li>「ユーザー」とは、本サービスを利用する個人又は法人を意味します。</li>
                            <li>「ユーザーコンテンツ」とは、ユーザーが本サービスを通じて投稿、送信、共有するテキスト、画像、動画、音声、ファイル等の一切の情報を意味します。</li>
                            <li>「知的財産権」とは、著作権、特許権、実用新案権、商標権、意匠権その他の知的財産権（それらの権利を取得し、又はそれらの権利につき登録等を出願する権利を含みます）を意味します。</li>
                        </ol>

                        <h2 className="text-lg font-bold mt-8 mb-3">第3条（利用登録）</h2>
                        <ol className="list-decimal list-inside space-y-2 mb-4">
                            <li>本サービスの利用を希望する者は、本規約を遵守することに同意し、当社の定める方法によって利用登録を申請するものとします。</li>
                            <li>当社は、前項に基づき利用登録の申請を行った者が、以下の各号のいずれかの事由に該当する場合は、利用登録を承認しないことがあります。
                                <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                                    <li>本規約に違反するおそれがあると当社が判断した場合</li>
                                    <li>当社に提供された登録情報の全部又は一部につき虚偽、誤記又は記載漏れがあった場合</li>
                                    <li>過去に本サービスの利用の登録を取り消された者である場合</li>
                                    <li>その他、当社が登録を適当でないと判断した場合</li>
                                </ul>
                            </li>
                        </ol>

                        <h2 className="text-lg font-bold mt-8 mb-3">第4条（アカウント情報の管理）</h2>
                        <ol className="list-decimal list-inside space-y-2 mb-4">
                            <li>ユーザーは、自己の責任において、本サービスに関するアカウント情報を適切に管理及び保管するものとし、これを第三者に利用させ、又は貸与、譲渡、名義変更、売買等をしてはならないものとします。</li>
                            <li>アカウント情報の管理不十分、使用上の過誤、第三者の使用等による損害の責任はユーザーが負うものとし、当社は一切の責任を負いません。</li>
                        </ol>

                        <h2 className="text-lg font-bold mt-8 mb-3">第5条（禁止事項）</h2>
                        <p className="mb-2">ユーザーは、本サービスの利用にあたり、以下の各号のいずれかに該当する行為又は該当すると当社が判断する行為をしてはなりません。</p>
                        <ol className="list-decimal list-inside space-y-2 mb-4">
                            <li>法令又は公序良俗に違反する行為</li>
                            <li>犯罪行為に関連する行為</li>
                            <li>当社、本サービスの他のユーザー、又は第三者の知的財産権、肖像権、プライバシーの権利、名誉、その他の権利又は利益を侵害する行為</li>
                            <li>本サービスのネットワーク又はシステム等に過度な負荷をかける行為</li>
                            <li>本サービスの運営を妨害するおそれのある行為</li>
                            <li>当社のネットワーク又はシステム等への不正アクセス</li>
                            <li>第三者に成りすます行為</li>
                            <li>本サービスの他のユーザーのアカウントを利用する行為</li>
                            <li>反社会的勢力等への利益供与</li>
                            <li>公序良俗に反する情報を本サービス上に投稿又は送信する行為</li>
                            <li>同一又は類似のメッセージを不特定多数のユーザーに送信する行為（当社の認めたものを除きます）</li>
                            <li>本サービスの他のユーザーの情報の収集</li>
                            <li>当社、本サービスの他のユーザー又は第三者に不利益、損害、不快感を与える行為</li>
                            <li>当社が定める方法以外の方法で本サービスの利用権を第三者に譲渡、貸与する行為</li>
                            <li>営業、宣伝、広告、勧誘等を目的とした本サービスの利用（当社の認めたものを除きます）</li>
                            <li>面識のない異性との出会いを目的とした利用</li>
                            <li>前各号の行為を直接又は間接に惹起し、又は容易にする行為</li>
                            <li>その他、当社が不適切と判断する行為</li>
                        </ol>

                        <h2 className="text-lg font-bold mt-8 mb-3">第6条（ユーザーコンテンツの取扱い）</h2>
                        <ol className="list-decimal list-inside space-y-2 mb-4">
                            <li>ユーザーは、自己の責任において、ユーザーコンテンツを本サービスに投稿又は送信するものとします。</li>
                            <li>ユーザーは、ユーザーコンテンツについて、自らが投稿その他送信することについての適法な権利を有していること、及びユーザーコンテンツが第三者の権利を侵害していないことについて、当社に対し表明し、保証するものとします。</li>
                            <li>ユーザーコンテンツに関する著作権は、当該ユーザーコンテンツを投稿したユーザーに留保されます。当社は、本サービスの提供、維持、改善又は本サービスのプロモーションに必要な範囲において、無償かつ非独占的に、ユーザーコンテンツを使用、複製、配布、派生物の作成、表示及び実行することができるものとします。</li>
                            <li>当社は、ユーザーが本規約に違反し、又は違反するおそれがあると当社が判断した場合、当社の裁量により、事前の通知なく、ユーザーコンテンツの全部又は一部を削除することができるものとします。</li>
                        </ol>

                        <h2 className="text-lg font-bold mt-8 mb-3">第7条（利用制限及び登録抹消）</h2>
                        <ol className="list-decimal list-inside space-y-2 mb-4">
                            <li>当社は、ユーザーが以下のいずれかに該当する場合には、事前の通知なく、ユーザーに対して、本サービスの全部若しくは一部の利用を制限し、又はユーザーとしての登録を抹消することができるものとします。
                                <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                                    <li>本規約のいずれかの条項に違反した場合</li>
                                    <li>登録事項に虚偽の事実があることが判明した場合</li>
                                    <li>支払停止若しくは支払不能となり、又は破産手続開始、民事再生手続開始、会社更生手続開始、特別清算開始若しくはこれらに類する手続の開始の申立てがあった場合</li>
                                    <li>6ヶ月以上本サービスの利用がない場合</li>
                                    <li>当社からの連絡に対し、相当の期間が経過しても返答がない場合</li>
                                    <li>その他、当社が本サービスの利用を適当でないと判断した場合</li>
                                </ul>
                            </li>
                            <li>前項各号のいずれかに該当した場合、ユーザーは、当社に対して負っている債務の一切について当然に期限の利益を失い、直ちに当社に対して全ての債務の支払を行わなければなりません。</li>
                        </ol>

                        <h2 className="text-lg font-bold mt-8 mb-3">第8条（保証の否認及び免責）</h2>
                        <ol className="list-decimal list-inside space-y-2 mb-4">
                            <li>当社は、本サービスがユーザーの特定の目的に適合すること、期待する機能・商品的価値・正確性・有用性を有すること、ユーザーによる本サービスの利用がユーザーに適用のある法令又は業界団体の内部規則等に適合すること、及び不具合が生じないことについて、何ら保証するものではありません。</li>
                            <li>当社は、本サービスに起因してユーザーに生じたあらゆる損害について一切の責任を負いません。ただし、本サービスに関する当社とユーザーとの間の契約が消費者契約法に定める消費者契約となる場合、この免責規定は適用されません。</li>
                            <li>前項ただし書に定める場合であっても、当社は、当社の過失（重過失を除きます）による債務不履行又は不法行為によりユーザーに生じた損害のうち特別な事情から生じた損害（当社又はユーザーが損害発生につき予見し、又は予見し得た場合を含みます）について一切の責任を負いません。</li>
                            <li>当社は、本サービスに関して、ユーザーと他のユーザー又は第三者との間において生じた取引、連絡又は紛争等について一切責任を負いません。</li>
                        </ol>

                        <h2 className="text-lg font-bold mt-8 mb-3">第9条（サービス内容の変更等）</h2>
                        <p className="mb-4">当社は、ユーザーに通知することなく、本サービスの内容を変更し又は本サービスの提供を中止することができるものとし、これによってユーザーに生じた損害について一切の責任を負いません。</p>

                        <h2 className="text-lg font-bold mt-8 mb-3">第10条（利用規約の変更）</h2>
                        <ol className="list-decimal list-inside space-y-2 mb-4">
                            <li>当社は、必要と判断した場合には、ユーザーに通知することなくいつでも本規約を変更することができるものとします。</li>
                            <li>変更後の本規約は、当社が別途定める場合を除いて、本サービス上に表示した時点より効力を生じるものとします。</li>
                            <li>ユーザーが、変更後の本規約の効力発生後に本サービスを利用した場合、変更後の本規約に同意したものとみなします。</li>
                        </ol>

                        <h2 className="text-lg font-bold mt-8 mb-3">第11条（個人情報の取扱い）</h2>
                        <p className="mb-4">当社は、本サービスの利用によって取得する個人情報については、当社の<a href="https://jsecurity.co.jp/privacypolicy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">プライバシーポリシー</a>に従い適切に取り扱うものとします。</p>

                        <h2 className="text-lg font-bold mt-8 mb-3">第12条（通知又は連絡）</h2>
                        <p className="mb-4">ユーザーと当社との間の通知又は連絡は、当社の定める方法によって行うものとします。当社は、ユーザーから、当社が別途定める方式に従った変更届け出がない限り、現在登録されている連絡先が有効なものとみなして当該連絡先へ通知又は連絡を行い、これらは、発信時にユーザーへ到達したものとみなします。</p>

                        <h2 className="text-lg font-bold mt-8 mb-3">第13条（権利義務の譲渡の禁止）</h2>
                        <p className="mb-4">ユーザーは、当社の書面による事前の承諾なく、利用契約上の地位又は本規約に基づく権利若しくは義務を第三者に譲渡し、又は担保に供することはできません。</p>

                        <h2 className="text-lg font-bold mt-8 mb-3">第14条（準拠法及び管轄裁判所）</h2>
                        <ol className="list-decimal list-inside space-y-2 mb-4">
                            <li>本規約の解釈にあたっては、日本法を準拠法とします。</li>
                            <li>本サービスに関して紛争が生じた場合には、当社の本店所在地を管轄する裁判所を専属的合意管轄裁判所とします。</li>
                        </ol>

                        <h2 className="text-lg font-bold mt-8 mb-3">附則</h2>
                        <p className="mb-4">本規約は、2025年11月3日から施行します。</p>

                        <h2 className="text-lg font-bold mt-8 mb-3">お問い合わせ</h2>
                        <p className="mb-2">本規約に関するお問い合わせは、以下の連絡先までお願いいたします。</p>
                        <p>メールアドレス：next@jsecurity.co.jp</p>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TermsModal;
